import { LoadingButton } from '@mui/lab';
import { ReactNode } from 'react';
import { Box, Button } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useLocales from '../hooks/useLocales';

interface Props {
    toggleMoreFilters: () => void;
    showMoreFilters: boolean;
    handleReset: () => void;
    handleSearch: () => void;
    loading: boolean;
    hasMoreFilters?: boolean;
    searchDisabled?: boolean;
    /** For forms that apply filters as they change: no Search button. */
    hideSearch?: boolean;
    /** Replaces "Reset", shown as written (sentence case). */
    resetLabel?: string;
    /** Disables every button in the group (e.g. while results load). */
    disabled?: boolean;
    /** Rendered after Search, e.g. a secondary action that must outlive `disabled`. */
    trailing?: ReactNode;
}

export default function SearchButtonGroup({
    toggleMoreFilters,
    showMoreFilters,
    handleReset,
    handleSearch,
    loading,
    hasMoreFilters,
    searchDisabled,
    disabled = false,
    trailing,
    hideSearch = false,
    resetLabel,
}: Props) {
    const { translate } = useLocales();

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { lg: 'row', sm: 'column', xs: 'column' },
            }}
        >
            {hasMoreFilters && (
                <Button
                    sx={{ width: 'max-content' }}
                    variant="text"
                    color="primary"
                    onClick={toggleMoreFilters}
                    disabled={disabled}
                    startIcon={showMoreFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    data-testid="search-more-filters"
                >
                    {showMoreFilters
                        ? translate('components.searchButtonGroup.lessFilters')
                        : translate('components.searchButtonGroup.moreFilters')}
                </Button>
            )}
            <Button
                variant="outlined"
                color="primary"
                onClick={handleReset}
                disabled={disabled}
                sx={{
                    marginX: { lg: 1, sm: 0, xs: 0 },
                    marginY: { lg: 0, sm: 1, xs: 1 },
                    // The theme capitalizes every word of button labels.
                    ...(resetLabel && { textTransform: 'none' }),
                }}
                data-testid="search-reset"
            >
                {resetLabel ?? translate('components.searchButtonGroup.reset')}
            </Button>
            {!hideSearch && (
                <LoadingButton
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    loading={loading}
                    disabled={disabled || searchDisabled}
                    sx={{ width: { lg: '100px', sm: 'auto' } }}
                    data-testid="search-submit"
                >
                    {translate('components.searchButtonGroup.search')}
                </LoadingButton>
            )}
            {trailing && (
                <Box sx={{ ml: { lg: 1, sm: 0, xs: 0 }, mt: { lg: 0, sm: 1, xs: 1 } }}>
                    {trailing}
                </Box>
            )}
        </Box>
    );
}
